import { useUiLanguage } from "@/i18n/useUiLanguage";
import { tr } from "@/i18n/copy";
import { useRef, useState } from "react";
import { Camera, ImagePlus, UserRound } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import AuthenticatedApiImage from "@/components/AuthenticatedApiImage";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function TeamProfilePhoto({
  path = "/me/profile-image",
  artistId,
  studioSlug,
  editable = false,
  className = "h-16 w-16 rounded-2xl",
  onUploaded,
}) {
  useUiLanguage();
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const busyRef = useRef(false);
  const [choosing, setChoosing] = useState(false);
  const [version, setVersion] = useState(0);
  const [uploading, setUploading] = useState(false);
  const resolvedPath = path ? `${path}${path.includes("?") ? "&" : "?"}v=${version}` : "";

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || busyRef.current) return;
    busyRef.current = true;
    setChoosing(false);
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      await api.post("/me/profile-image", data);
      setVersion((value) => value + 1);
      toast.success(tr("Foto profilo aggiornata"));
      onUploaded?.();
      window.dispatchEvent(new Event("tps:data-changed"));
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      busyRef.current = false;
      setUploading(false);
    }
  };

  const accountPhoto = <AuthenticatedApiImage
      path={resolvedPath}
      alt={tr("Foto profilo")}
      className="h-full w-full object-cover"
      fallback={<span className="grid h-full w-full place-items-center">{editable ? <Camera className="h-1/2 w-1/2 text-primary"/> : <UserRound className="h-1/2 w-1/2 text-primary"/>}</span>}
    />;
  const photo = artistId && studioSlug ? <AuthenticatedApiImage
    key={`${studioSlug}:${artistId}`}
    path={`/public/studios/${encodeURIComponent(studioSlug)}/artists/${encodeURIComponent(artistId)}/image?v=${version}`}
    alt={tr("Foto profilo")}
    className="h-full w-full object-cover"
    fallback={accountPhoto}
  /> : accountPhoto;

  return <div className={`relative shrink-0 overflow-hidden border bg-primary/10 ${className}`}>
    {editable ? <>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} className="hidden" onChange={upload}/>
      <input ref={cameraRef} type="file" accept="image/*" capture="user" disabled={uploading} className="hidden" onChange={upload}/>
      <Dialog open={choosing} onOpenChange={setChoosing}>
        <DialogTrigger asChild><button type="button" disabled={uploading} aria-label={tr("Scatta o scegli foto profilo")} title={tr("Scatta o scegli foto profilo")} className="block h-full w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary disabled:cursor-wait">
          {photo}
        </button></DialogTrigger>
        <DialogContent className="max-w-sm">
          <DialogTitle>{tr("Foto profilo")}</DialogTitle>
          <DialogDescription>{tr("Scatta o scegli foto profilo")}</DialogDescription>
          <div className="grid gap-3">
            <Button type="button" variant="outline" disabled={uploading} className="min-h-12 justify-start" onClick={() => cameraRef.current?.click()}><Camera className="mr-2 h-4 w-4"/>{tr("Scatta")}</Button>
            <Button type="button" variant="outline" disabled={uploading} className="min-h-12 justify-start" onClick={() => inputRef.current?.click()}><ImagePlus className="mr-2 h-4 w-4"/>{tr("Scegli foto")} / {tr("Logo")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </> : photo}
  </div>;
}
